import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import Tree from 'react-d3-tree';
import { trackFeatureUsage, trackSearch, trackEvent } from '../utils/analytics';

const Hierarchy = () => {
  const { user } = useAuth();
  const [hierarchyData, setHierarchyData] = useState(null);
  const [treeData, setTreeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  
  // Search functionality state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUserStats, setSelectedUserStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    fetchHierarchy();
    // Set initial translate to center the tree
    setTranslate({ x: dimensions.width / 2, y: 50 });
    // Track hierarchy view
    trackFeatureUsage('Network Hierarchy', 'view');
  }, [dimensions.width]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.search-container')) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchHierarchy = async (userId = null) => {
    try {
      const endpoint = userId ? `/api/hierarchy/user-network/${userId}` : '/api/hierarchy/my-network';
      const response = await axios.get(endpoint);
      const data = response.data;
      setHierarchyData(data);
      
      // Convert hierarchy data to react-d3-tree format
      const convertedData = convertToTreeFormat(data, userId || user.id);
      setTreeData(convertedData);
    } catch (error) {
      console.error('Failed to fetch hierarchy:', error);
      setError('Failed to load network hierarchy. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    // Track search usage
    trackSearch(query, 'hierarchy_users');

    setSearchLoading(true);
    try {
      const response = await axios.get(`/api/hierarchy/search-users?query=${encodeURIComponent(query)}`);
      setSearchResults(response.data);
      setShowSearchResults(true);
      
      // Track search results
      trackEvent('search_results', {
        event_category: 'Search',
        event_label: 'Hierarchy User Search',
        value: response.data.length
      });
    } catch (error) {
      console.error('Failed to search users:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    searchUsers(query);
  };

  const handleUserSelect = (selectedUser) => {
    setSelectedUser(selectedUser);
    setSearchQuery(selectedUser.fullName);
    setShowSearchResults(false);
  };

  const handleShowUserHierarchy = () => {
    if (selectedUser) {
      trackEvent('view_user_hierarchy', {
        event_category: 'Hierarchy',
        event_label: selectedUser.fullName,
        user_id: selectedUser.id
      });
      setLoading(true);
      fetchHierarchy(selectedUser.id);
      fetchSelectedUserStats(selectedUser.id);
    }
  };

  const fetchSelectedUserStats = async (userId) => {
    setStatsLoading(true);
    try {
      const response = await axios.get(`/api/dashboard/user-stats/${userId}`);
      setSelectedUserStats(response.data);
    } catch (error) {
      console.error('Failed to fetch user stats:', error);
      setSelectedUserStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleResetToMyNetwork = () => {
    setSelectedUser(null);
    setSelectedUserStats(null);
    setSearchQuery('');
    setLoading(true);
    fetchHierarchy();
  };

  // Convert API response to react-d3-tree format
  const convertToTreeFormat = (node, currentUserId) => {
    const isCurrentUser = node.id === currentUserId;
    const isRoleGroup = node.id < 0; // Negative IDs indicate role groups
    
    console.log('Converting node:', node.fullName, 'ID:', node.id, 'Children:', node.children?.length || 0);
    
    return {
      name: node.fullName || node.email,
      attributes: {
        email: node.email,
        role: node.memberRole?.displayName || 'No Role',
        joinedAt: node.joinedAt ? new Date(node.joinedAt).toLocaleDateString() : '',
        isCurrentUser: isCurrentUser,
        isRoleGroup: isRoleGroup,
        id: node.id,
        activeChildrenCount: node.activeChildrenCount || 0,
        inactiveChildrenCount: node.inactiveChildrenCount || 0,
        status: node.status || 'ACTIVE'
      },
      children: node.children && node.children.length > 0 
        ? node.children.map(child => convertToTreeFormat(child, currentUserId))
        : []
    };
  };

  // Get node color based on member role
  const getNodeColor = (memberRoleDisplayName, isInactive, isCurrentUser) => {
    if (isInactive) return '#F56565'; // Red for inactive users
    if (isCurrentUser) return '#2C5282'; // Dark blue for current user
    
    // Color scheme based on member role display name
    const roleDisplayName = memberRoleDisplayName?.toUpperCase();
    switch (roleDisplayName) {
      case 'SUPERVISOR':
        return '#4A90E2'; // Blue
      case 'WORLD TEAM':
      case 'AWT TEAM':
        return '#2D3748'; // Black
      case 'GET TEAM':
        return '#ED64A6'; // Pink
      case 'MILLENNIAL TEAM':
        return '#48BB78'; // Green
      case 'ASSOCIATE':
        return '#9F7AEA'; // Purple
      case 'CUSTOMER':
        return '#F6AD55'; // Orange
      default:
        return '#A0AEC0'; // Gray for unknown roles
    }
  };

  const getNodeStrokeColor = (memberRoleDisplayName, isInactive, isCurrentUser) => {
    if (isInactive) return '#E53E3E'; // Red stroke for inactive
    if (isCurrentUser) return '#1A365D'; // Dark blue stroke for current user
    
    // Darker version of fill color for stroke
    const roleDisplayName = memberRoleDisplayName?.toUpperCase();
    switch (roleDisplayName) {
      case 'SUPERVISOR':
        return '#2C5282'; // Darker blue
      case 'WORLD TEAM':
      case 'AWT TEAM':
        return '#1A202C'; // Darker black
      case 'GET TEAM':
        return '#B83280'; // Darker pink
      case 'MILLENNIAL TEAM':
        return '#38A169'; // Darker green
      case 'ASSOCIATE':
        return '#805AD5'; // Darker purple
      case 'CUSTOMER':
        return '#DD6B20'; // Darker orange
      default:
        return '#718096'; // Darker gray
    }
  };

  // Custom node rendering
  const renderCustomNodeElement = useCallback(({ nodeDatum, toggleNode }) => {
    const isCurrentUser = nodeDatum.attributes?.isCurrentUser;
    const isRoleGroup = nodeDatum.attributes?.email?.startsWith('role-group-');
    const hasChildren = nodeDatum.children && nodeDatum.children.length > 0;
    const isInactive = nodeDatum.attributes?.status === 'INACTIVE' || nodeDatum.attributes?.status === 'BLOCKED';
    const memberRoleDisplayName = nodeDatum.attributes?.role;
    
    const fillColor = isRoleGroup ? '#87CEEB' : getNodeColor(memberRoleDisplayName, isInactive, isCurrentUser);
    const strokeColor = isRoleGroup ? '#4A90E2' : getNodeStrokeColor(memberRoleDisplayName, isInactive, isCurrentUser);
    
    return (
      <g>
        {/* Node shape - rectangle for role groups, circle for individuals */}
        {isRoleGroup ? (
          <rect
            x={-30}
            y={-15}
            width={60}
            height={30}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={2}
            rx={5}
            onClick={toggleNode}
            style={{ cursor: 'pointer' }}
          />
        ) : (
          <circle
            r={20}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={2}
            onClick={toggleNode}
            style={{ cursor: 'pointer' }}
          />
        )}
        
        {/* Icon */}
        <text
          x={0}
          y={5}
          textAnchor="middle"
          fill={isRoleGroup ? "#2C5282" : "white"}
          fontSize={isRoleGroup ? "14" : "12"}
          fontWeight="bold"
        >
          {isRoleGroup ? "👥" : "👤"}
        </text>
        
        {/* Name label */}
        <text
          x={0}
          y={-35}
          textAnchor="middle"
          fill={isInactive ? "#C53030" : "#1A202C"}
          fontSize={isRoleGroup ? "12" : "14"}
          fontWeight="bold"
          fontFamily="Arial, sans-serif"
        >
          {nodeDatum.name}
        </text>
        
        {/* Role label - only for individual users */}
        {!isRoleGroup && (
          <text
            x={0}
            y={45}
            textAnchor="middle"
            fill={isInactive ? "#C53030" : "#2D3748"}
            fontSize="12"
            fontWeight="600"
            fontFamily="Arial, sans-serif"
          >
            {nodeDatum.attributes?.role}
          </text>
        )}
        
        {/* Joined date - only for individual users */}
        {!isRoleGroup && nodeDatum.attributes?.joinedAt && (
          <text
            x={0}
            y={58}
            textAnchor="middle"
            fill={isInactive ? "#C53030" : "#4A5568"}
            fontSize="10"
            fontWeight="500"
            fontFamily="Arial, sans-serif"
          >
            Joined: {nodeDatum.attributes.joinedAt}
          </text>
        )}
        
        {/* Current user indicator */}
        {isCurrentUser && (
          <text
            x={0}
            y={-50}
            textAnchor="middle"
            fill="#E53E3E"
            fontSize="12"
            fontWeight="bold"
            fontFamily="Arial, sans-serif"
          >
            YOU
          </text>
        )}
        
        {/* Inactive status indicator */}
        {isInactive && !isRoleGroup && (
          <text
            x={0}
            y={-50}
            textAnchor="middle"
            fill="#E53E3E"
            fontSize="10"
            fontWeight="bold"
            fontFamily="Arial, sans-serif"
          >
            INACTIVE
          </text>
        )}
        
        {/* Active/Inactive counts for immediate children */}
        {(nodeDatum.attributes?.activeChildrenCount > 0 || nodeDatum.attributes?.inactiveChildrenCount > 0) && (
          <g>
            <text
              x={-15}
              y={75}
              textAnchor="middle"
              fill="#48BB78"
              fontSize="10"
              fontWeight="bold"
              fontFamily="Arial, sans-serif"
            >
              ✓{nodeDatum.attributes.activeChildrenCount}
            </text>
            <text
              x={15}
              y={75}
              textAnchor="middle"
              fill="#F56565"
              fontSize="10"
              fontWeight="bold"
              fontFamily="Arial, sans-serif"
            >
              ✗{nodeDatum.attributes.inactiveChildrenCount}
            </text>
          </g>
        )}
        
        {/* Expand/Collapse indicator for groups */}
        {hasChildren && (
          <text
            x={25}
            y={-10}
            textAnchor="middle"
            fill="#4A90E2"
            fontSize="12"
            fontWeight="bold"
            fontFamily="Arial, sans-serif"
          >
            {nodeDatum.__rd3t?.collapsed ? "+" : "-"}
          </text>
        )}
      </g>
    );
  }, []);

  // Handle container resize
  useEffect(() => {
    const updateDimensions = () => {
      const container = document.getElementById('tree-container');
      if (container) {
        setDimensions({
          width: container.offsetWidth,
          height: Math.max(600, window.innerHeight - 200)
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  if (loading) {
    return (
      <div className="page">
        <div className="loading">Loading your network...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="alert alert-error">{error}</div>
        <button onClick={fetchHierarchy} className="btn btn-primary">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="page">
      <h1 className="page-title">
        {selectedUser ? `${selectedUser.fullName}'s Network` : 'My Network Hierarchy'}
      </h1>
      
      {user?.systemRoles?.some(role => role.code === 'SYSTEM_ADMIN') && (
        <div style={{
          marginBottom: '20px',
          padding: '15px',
          background: 'linear-gradient(135deg, #4A90E2 0%, #2C5282 100%)',
          color: 'white',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '5px' }}>
            🔑 Super Admin Mode
          </div>
          <div style={{ fontSize: '14px', opacity: '0.9' }}>
            You can search and view the network of any user in the system
          </div>
        </div>
      )}
      
      {/* User Search Section */}
      <div className="search-container" style={{
        marginBottom: '20px',
        padding: '20px',
        background: 'var(--white)',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(74, 144, 226, 0.1)'
      }}>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1', minWidth: '300px' }}>
            <label htmlFor="userSearch" className="form-label">
              Search User in Network
            </label>
            <input
              id="userSearch"
              type="text"
              className="form-input"
              placeholder={user?.systemRoles?.some(role => role.code === 'SYSTEM_ADMIN') 
                ? "Search any user in the system..." 
                : "Type user name or email..."}
              value={searchQuery}
              onChange={handleSearchChange}
              style={{ paddingRight: searchLoading ? '40px' : '12px' }}
            />
            {searchLoading && (
              <div style={{
                position: 'absolute',
                right: '12px',
                top: '32px',
                color: 'var(--primary-blue)'
              }}>
                ⏳
              </div>
            )}
            
            {/* Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: '0',
                right: '0',
                background: 'var(--white)',
                border: '1px solid var(--gray-medium)',
                borderRadius: '6px',
                boxShadow: '0 4px 15px rgba(74, 144, 226, 0.2)',
                zIndex: 1000,
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {searchResults.map((searchUser) => (
                  <div
                    key={searchUser.id}
                    onClick={() => handleUserSelect(searchUser)}
                    style={{
                      padding: '12px',
                      borderBottom: '1px solid var(--gray-light)',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--pale-blue)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    <div style={{ fontWeight: '500', color: 'var(--dark-blue)' }}>
                      {searchUser.fullName}
                      {user?.systemRoles?.some(role => role.code === 'SYSTEM_ADMIN') && (
                        <span style={{ 
                          marginLeft: '8px', 
                          fontSize: '10px', 
                          background: 'var(--primary-blue)', 
                          color: 'white', 
                          padding: '2px 6px', 
                          borderRadius: '10px' 
                        }}>
                          ID: {searchUser.id}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                      {searchUser.email} • {searchUser.memberRole} • {searchUser.status}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              className="btn btn-primary"
              onClick={handleShowUserHierarchy}
              disabled={!selectedUser || loading}
            >
              {loading ? 'Loading...' : 'Show Network'}
            </button>
            
            {selectedUser && (
              <button
                className="btn btn-secondary"
                onClick={handleResetToMyNetwork}
                disabled={loading}
              >
                My Network
              </button>
            )}
          </div>
        </div>
        
        {selectedUser && (
          <div style={{
            marginTop: '15px',
            padding: '10px',
            background: user?.systemRoles?.some(role => role.code === 'SYSTEM_ADMIN') 
              ? 'linear-gradient(135deg, #E6F3FF 0%, #B3D9FF 100%)' 
              : 'var(--pale-blue)',
            borderRadius: '6px',
            fontSize: '14px',
            border: user?.systemRoles?.some(role => role.code === 'SYSTEM_ADMIN') 
              ? '2px solid var(--primary-blue)' 
              : '1px solid var(--light-blue)'
          }}>
            <strong>
              {user?.systemRoles?.some(role => role.code === 'SYSTEM_ADMIN') ? '🔍 Viewing Network:' : 'Selected:'}
            </strong> {selectedUser.fullName} ({selectedUser.email}) - {selectedUser.memberRole}
            {user?.systemRoles?.some(role => role.code === 'SYSTEM_ADMIN') && (
              <div style={{ fontSize: '12px', marginTop: '5px', color: 'var(--primary-blue)' }}>
                Admin Mode: You can view any user's complete network hierarchy
              </div>
            )}
          </div>
        )}
      </div>

      {/* Selected User Stats Summary */}
      {selectedUser && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
            {selectedUser.fullName}'s Network Summary
          </h3>
          
          {statsLoading ? (
            <div className="loading" style={{ padding: '20px' }}>
              Loading stats...
            </div>
          ) : selectedUserStats ? (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '20px' 
            }}>
              <div className="stat-card" style={{ margin: 0 }}>
                <div className="stat-number">{selectedUserStats.totalDownline}</div>
                <div className="stat-label">Total Network Size</div>
              </div>
              
              <div className="stat-card" style={{ margin: 0 }}>
                <div className="stat-number">{selectedUserStats.directMembers}</div>
                <div className="stat-label">Direct Members</div>
              </div>
              
              <div className="stat-card" style={{ margin: 0 }}>
                <div className="stat-number" style={{ color: 'var(--success)' }}>
                  {selectedUserStats.activeMembers}
                </div>
                <div className="stat-label">Active Members</div>
              </div>
              
              <div className="stat-card" style={{ margin: 0 }}>
                <div className="stat-number" style={{ color: 'var(--error)' }}>
                  {selectedUserStats.inactiveMembers}
                </div>
                <div className="stat-label">Inactive Members</div>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: 'var(--gray-dark)' }}>
              Unable to load stats for this user
            </div>
          )}
        </div>
      )}
      
      <div className="hierarchy-container">
        {treeData ? (
          <div>
            {/* Tree Controls */}
            <div className="tree-controls" style={{ 
              marginBottom: '20px', 
              padding: '15px', 
              background: 'var(--pale-blue)', 
              borderRadius: '8px',
              display: 'flex',
              gap: '15px',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              <div style={{ color: 'var(--dark-blue)', fontWeight: 'bold' }}>
                Network Tree View (Grouped by Roles)
              </div>
              <div style={{ fontSize: '14px', color: 'var(--gray-dark)' }}>
                👥 Role Groups • 👤 Individual Members • ✓ Active • ✗ Inactive • 🔴 Inactive Users • Click to expand/collapse • Drag to pan • Scroll to zoom
              </div>
              <button 
                className="btn btn-secondary"
                onClick={() => setTranslate({ x: dimensions.width / 2, y: 50 })}
                style={{ marginLeft: 'auto' }}
              >
                Reset View
              </button>
            </div>

            {/* Color Legend */}
            <div style={{
              marginBottom: '15px',
              padding: '10px',
              background: 'var(--white)',
              borderRadius: '6px',
              border: '1px solid var(--gray-medium)',
              fontSize: '12px'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px', color: 'var(--dark-blue)' }}>
                Role Color Legend:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#48BB78' }}></div>
                  <span>Millennial Team</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#ED64A6' }}></div>
                  <span>GET Team</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#2D3748' }}></div>
                  <span>World/AWT Team</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#4A90E2' }}></div>
                  <span>Supervisor</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#9F7AEA' }}></div>
                  <span>Associate</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#F6AD55' }}></div>
                  <span>Customer</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#F56565' }}></div>
                  <span>Inactive</span>
                </div>
              </div>
            </div>

            {/* Tree Container */}
            <div 
              id="tree-container"
              className="tree-wrapper"
              style={{
                width: '100%',
                height: `${dimensions.height}px`,
                border: '2px solid var(--light-blue)',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #f8fdff 0%, #e6f3ff 100%)',
                overflow: 'hidden'
              }}
            >
              <Tree
                data={treeData}
                translate={translate}
                orientation="vertical"
                pathFunc="step"
                renderCustomNodeElement={renderCustomNodeElement}
                separation={{ siblings: 2, nonSiblings: 2 }}
                nodeSize={{ x: 220, y: 160 }}
                zoom={0.7}
                scaleExtent={{ min: 0.2, max: 2 }}
                enableLegacyTransitions={true}
                transitionDuration={500}
                collapsible={true}
                initialDepth={undefined}
                depthFactor={160}
                styles={{
                  links: {
                    stroke: '#4A90E2',
                    strokeWidth: 2,
                  },
                  nodes: {
                    node: {
                      circle: {
                        fill: '#4A90E2',
                        stroke: '#2C5282',
                        strokeWidth: 2,
                      },
                      name: {
                        stroke: 'none',
                        fill: '#1A202C',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        fontFamily: 'Arial, sans-serif',
                      },
                      attributes: {
                        stroke: 'none',
                        fill: '#2D3748',
                        fontSize: '12px',
                        fontWeight: '600',
                        fontFamily: 'Arial, sans-serif',
                      },
                    },
                    leafNode: {
                      circle: {
                        fill: '#87CEEB',
                        stroke: '#4A90E2',
                        strokeWidth: 2,
                      },
                    },
                  },
                }}
              />
            </div>

            {/* Network Statistics */}
            <div className="tree-stats" style={{
              marginTop: '20px',
              padding: '15px',
              background: 'var(--white)',
              borderRadius: '8px',
              border: '1px solid var(--gray-medium)',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '15px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--primary-blue)' }}>
                  {countTotalNodes(treeData)}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>Total Members</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--primary-blue)' }}>
                  {treeData.children?.length || 0}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>Direct Reports</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--primary-blue)' }}>
                  {getMaxDepth(treeData)}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>Max Depth</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <p style={{ color: 'var(--gray-dark)' }}>
              No network data available.
            </p>
          </div>
        )}
      </div>

      {user.systemRoles?.some(role => role.code === 'SYSTEM_VIEWER' || role.code === 'SYSTEM_EDITOR') && (
        <div className="card mt-20">
          <h3 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
            Network Management
          </h3>
          <p style={{ marginBottom: '15px', color: 'var(--gray-dark)' }}>
            You have system permissions to view and manage network hierarchy.
          </p>
          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
            <button className="btn btn-secondary">
              Export Network Data
            </button>
            {user.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR') && (
              <button className="btn btn-primary">
                Add New Member
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // Helper functions
  function countTotalNodes(node) {
    if (!node) return 0;
    let count = 1;
    if (node.children) {
      node.children.forEach(child => {
        count += countTotalNodes(child);
      });
    }
    return count;
  }

  function getMaxDepth(node, depth = 0) {
    if (!node || !node.children || node.children.length === 0) {
      return depth;
    }
    let maxChildDepth = depth;
    node.children.forEach(child => {
      const childDepth = getMaxDepth(child, depth + 1);
      maxChildDepth = Math.max(maxChildDepth, childDepth);
    });
    return maxChildDepth;
  }
};

export default Hierarchy;
